import { proxy } from 'valtio'
import { appStorage } from '../appStorageProvider'
import * as authApi from '../../api/utopiaAuthApi'

export type UtopiaAuthUser = {
  username: string
  gameUsername: string
  isGuest?: boolean
  email?: string
}

const LEGACY_TOKEN_KEY = 'utopia_auth_token'

export type UtopiaIpGameSession = {
  ipMasked: string | null
  requestIpMasked?: string | null
  expiresAt: number
  timeoutMinutes: number
}

export const utopiaAuthState = proxy({
  user: null as UtopiaAuthUser | null,
  loading: false,
  checked: false,
  ipGameSession: null as UtopiaIpGameSession | null,
})

export type UtopiaMicrosoftVerifiedProfile = {
  name: string
  utopiaAccountExists: boolean
  utopiaUsername: string
  proofToken: string
  proofExpiresAt: number
}

export const utopiaAuthUiState = proxy({
  navDropdownOpen: false,
  microsoftVerifiedProfile: null as UtopiaMicrosoftVerifiedProfile | null,
})

export function openNavAuthDropdown () {
  utopiaAuthUiState.navDropdownOpen = true
}

export function closeNavAuthDropdown () {
  utopiaAuthUiState.navDropdownOpen = false
}

export function toggleNavAuthDropdown () {
  utopiaAuthUiState.navDropdownOpen = !utopiaAuthUiState.navDropdownOpen
}

function clearLegacySessionStorage () {
  try {
    sessionStorage.removeItem(LEGACY_TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

export function setUtopiaAuthSession (data: { user: UtopiaAuthUser }) {
  utopiaAuthState.user = data.user
  clearLegacySessionStorage()
  appStorage.username = data.user.gameUsername
}

export function patchUtopiaAuthUser (patch: Partial<UtopiaAuthUser>) {
  if (!utopiaAuthState.user) return
  utopiaAuthState.user = { ...utopiaAuthState.user, ...patch }
}

export function clearUtopiaAuthSession () {
  utopiaAuthState.user = null
  utopiaAuthState.ipGameSession = null
  clearLegacySessionStorage()
  utopiaAuthUiState.microsoftVerifiedProfile = null
}

export function setUtopiaIpGameSession (data: UtopiaIpGameSession) {
  utopiaAuthState.ipGameSession = data
}

export function clearUtopiaIpGameSession () {
  utopiaAuthState.ipGameSession = null
}

/** Charge l'état session IP depuis la base (après login ou rafraîchissement). */
export async function refreshUtopiaIpGameSession () {
  if (!utopiaAuthState.user) {
    clearUtopiaIpGameSession()
    return
  }
  try {
    const status = await authApi.fetchIpGameSession()
    if (
      status.active
      && status.expiresAt != null
      && status.timeoutMinutes != null
    ) {
      setUtopiaIpGameSession({
        ipMasked: status.ipMasked ?? null,
        requestIpMasked: status.requestIpMasked ?? null,
        expiresAt: status.expiresAt,
        timeoutMinutes: status.timeoutMinutes,
      })
    } else {
      clearUtopiaIpGameSession()
    }
  } catch {
    clearUtopiaIpGameSession()
  }
}

export async function restoreUtopiaAuthSession () {
  clearLegacySessionStorage()
  utopiaAuthState.loading = true
  try {
    const user = await authApi.fetchAuthMe()
    if (user) {
      setUtopiaAuthSession({ user })
      await refreshUtopiaIpGameSession()
    } else {
      clearUtopiaAuthSession()
    }
  } finally {
    utopiaAuthState.loading = false
    utopiaAuthState.checked = true
  }
}

export async function logoutUtopiaAuth () {
  await authApi.logoutAuth()
  clearUtopiaAuthSession()
}
